import { useState, useEffect, useRef } from 'react';
import { CreditCard, Building2, X, Check, ChevronDown, Plus, FileText, FilePlus, Download, Mail, Loader2, Search, Trash2, Power, RefreshCw } from 'lucide-react';
import { adminApi } from '../../services/api';

interface Subscription {
  id: string;
  tenantId: string;
  status: 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | 'UNPAID';
  interval: 'MONTHLY' | 'YEARLY';
  pricingType?: 'ONESHOT' | 'YEARLY';
  quantity: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt?: string;
  cancelAtPeriodEnd: boolean;
  autoRenewal?: boolean;
  createdAt: string;
  startDate?: string;
  expiresAt?: string;
  notes?: string;
  deviceIds?: string[];
  alertsEnabled?: boolean;
  alertDays?: number[];
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
  plan: {
    id: string;
    name: string;
    displayName: string;
    priceMonthly: number;
    priceYearly: number;
    priceOneShot?: number;
  };
}

interface Plan {
  id: string;
  name: string;
  displayName: string;
  priceYearly: number;
  priceOneShot: number;
}

type Period = 'TRIAL' | 'MONTHLY' | 'YEARLY' | 'ONESHOT';

interface EditForm {
  planId: string;
  quantity: number;
  period: Period;
  status: string;
  autoRenewal: boolean;
  alertsEnabled: boolean;
  alertDays: number[];
  startDate: string;
  expiresAt: string;
  notes: string;
  deviceIds: string[];
}

interface TenantDevice {
  id: string;
  name: string;
  plate: string | null;
  imei: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string | null;
  amountDue: number;
  status: string;
  createdAt: string;
  dueDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
}

export default function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Global stats from backend (across all pages)
  const [stats, setStats] = useState({
    totalAll: 0,
    activeCount: 0,
    trialingCount: 0,
    pastDueCount: 0,
    activeVehicles: 0,
    annualRevenue: 0,
  });

  const [plans, setPlans] = useState<Plan[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSub, setEditingSub] = useState<Subscription | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    planId: '',
    quantity: 1,
    period: 'YEARLY',
    status: 'ACTIVE',
    autoRenewal: true,
    alertsEnabled: true,
    alertDays: [30, 15, 7],
    startDate: '',
    expiresAt: '',
    notes: '',
    deviceIds: [],
  });
  const [saving, setSaving] = useState(false);

  // Devices du tenant pour le multi-select
  const [tenantDevices, setTenantDevices] = useState<TenantDevice[]>([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [takenDeviceIds, setTakenDeviceIds] = useState<Set<string>>(new Set());
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState('');
  const deviceDropdownRef = useRef<HTMLDivElement>(null);
  const createDeviceDropdownRef = useRef<HTMLDivElement>(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [createForm, setCreateForm] = useState<EditForm & { tenantId: string }>({
    tenantId: '',
    planId: '',
    quantity: 1,
    period: 'YEARLY',
    status: 'ACTIVE',
    autoRenewal: true,
    alertsEnabled: true,
    alertDays: [30, 15, 7],
    startDate: new Date().toISOString().split('T')[0],
    expiresAt: '',
    notes: '',
    deviceIds: [],
  });
  const [creating, setCreating] = useState(false);

  // Multi-selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === subscriptions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(subscriptions.map((s) => s.id)));
    }
  };

  const handleBulkToggleActive = async (isActive: boolean) => {
    if (selectedIds.size === 0) return;
    const label = isActive ? 'activer' : 'désactiver';
    if (!confirm(`Voulez-vous ${label} ${selectedIds.size} abonnement(s) ?`)) return;
    setBulkLoading(true);
    try {
      await adminApi.bulkToggleActiveSubscriptions([...selectedIds], isActive);
      setSelectedIds(new Set());
      loadSubscriptions();
    } catch (err) {
      console.error('Erreur bulk toggle:', err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkToggleRenewal = async (autoRenewal: boolean) => {
    if (selectedIds.size === 0) return;
    const label = autoRenewal ? 'activer' : 'désactiver';
    if (!confirm(`Voulez-vous ${label} le renouvellement automatique pour ${selectedIds.size} abonnement(s) ?`)) return;
    setBulkLoading(true);
    try {
      await adminApi.bulkToggleRenewalSubscriptions([...selectedIds], autoRenewal);
      setSelectedIds(new Set());
      loadSubscriptions();
    } catch (err) {
      console.error('Erreur bulk toggle renewal:', err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Supprimer définitivement ${selectedIds.size} abonnement(s) ? Cette action est irréversible.`)) return;
    setBulkLoading(true);
    try {
      await adminApi.bulkDeleteSubscriptions([...selectedIds]);
      setSelectedIds(new Set());
      loadSubscriptions();
    } catch (err) {
      console.error('Erreur bulk delete:', err);
    } finally {
      setBulkLoading(false);
    }
  };

  // Invoice modal state
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [invoicesSub, setInvoicesSub] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [sendingInvoiceId, setSendingInvoiceId] = useState<string | null>(null);

  useEffect(() => {
    loadSubscriptions();
  }, [statusFilter, searchQuery, page, sortBy, sortOrder]);

  useEffect(() => {
    adminApi.getPlans().then((res) => {
      setPlans(Array.isArray(res.data) ? res.data : []);
    }).catch((err) => console.error('Erreur chargement plans:', err));
    adminApi.getTenants({ limit: 200 }).then((res) => {
      const data = res.data?.data || res.data?.tenants || res.data;
      setTenants(Array.isArray(data) ? data : []);
    }).catch((err) => console.error('Erreur chargement tenants:', err));
  }, []);

  // Close device dropdown on outside click
  useEffect(() => {
    if (!showDeviceDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inEdit = deviceDropdownRef.current?.contains(target);
      const inCreate = createDeviceDropdownRef.current?.contains(target);
      if (!inEdit && !inCreate) {
        setShowDeviceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDeviceDropdown]);

  // Auto-calcul de la date d'expiration selon la période
  const calcExpiresAt = (startDate: string, period: Period): string => {
    if (!startDate) return '';
    const d = new Date(startDate);
    switch (period) {
      case 'TRIAL':
        d.setDate(d.getDate() + 15);
        break;
      case 'MONTHLY':
        d.setMonth(d.getMonth() + 1);
        break;
      case 'YEARLY':
        d.setFullYear(d.getFullYear() + 1);
        break;
      case 'ONESHOT':
        return ''; // Pas de date d'expiration pour achat unique
    }
    return d.toISOString().split('T')[0];
  };

  const loadSubscriptions = async () => {
    try {
      const res = await adminApi.getSubscriptions({
        status: statusFilter || undefined,
        search: searchQuery || undefined,
        page,
        limit,
        sortBy,
        sortOrder,
      });
      const data = res.data?.subscriptions || res.data?.data || res.data;
      setSubscriptions(Array.isArray(data) ? data : []);
      setTotal(res.data?.total || res.data?.pagination?.total || (Array.isArray(data) ? data.length : 0));
      if (res.data?.stats) {
        setStats(res.data.stats);
      }
    } catch (error) {
      console.error('Erreur chargement abonnements:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTenantDevices = async (tenantId: string, excludeSubscriptionId?: string) => {
    try {
      const [devicesRes, subsRes] = await Promise.all([
        adminApi.getDevices({ tenantId, limit: 200 }),
        adminApi.getSubscriptions({ tenantId, limit: 200 }),
      ]);
      const devices = devicesRes.data?.data || devicesRes.data?.devices || devicesRes.data;
      setTenantDevices(Array.isArray(devices) ? devices : []);

      // Construire le set des deviceIds déjà pris par d'autres abonnements
      const subs = subsRes.data?.subscriptions || subsRes.data?.data || subsRes.data;
      const taken = new Set<string>();
      if (Array.isArray(subs)) {
        for (const sub of subs) {
          // Exclure l'abonnement en cours d'édition
          if (excludeSubscriptionId && sub.id === excludeSubscriptionId) continue;
          if (Array.isArray(sub.deviceIds)) {
            for (const did of sub.deviceIds) {
              taken.add(did);
            }
          }
        }
      }
      setTakenDeviceIds(taken);
    } catch (error) {
      console.error('Erreur chargement devices:', error);
      setTenantDevices([]);
      setTakenDeviceIds(new Set());
    }
  };

  const openEdit = (sub: Subscription) => {
    setEditingSub(sub);
    const subDeviceIds = sub.deviceIds || [];
    // Déduire la période : trial si statut TRIALING, sinon selon pricingType/interval
    let period: Period = 'YEARLY';
    if (sub.status === 'TRIALING') {
      period = 'TRIAL';
    } else if (sub.pricingType === 'ONESHOT') {
      period = 'ONESHOT';
    } else if (sub.interval === 'MONTHLY') {
      period = 'MONTHLY';
    }
    setEditForm({
      planId: sub.plan.id,
      quantity: sub.quantity || 1,
      period,
      status: sub.status,
      autoRenewal: sub.autoRenewal !== false,
      alertsEnabled: sub.alertsEnabled !== false,
      alertDays: sub.alertDays || [30, 15, 7],
      startDate: sub.startDate ? sub.startDate.split('T')[0] : (sub.currentPeriodStart ? sub.currentPeriodStart.split('T')[0] : ''),
      expiresAt: sub.expiresAt ? sub.expiresAt.split('T')[0] : (sub.currentPeriodEnd ? sub.currentPeriodEnd.split('T')[0] : ''),
      notes: sub.notes || '',
      deviceIds: subDeviceIds,
    });
    setSelectedDeviceIds(subDeviceIds);
    setDeviceSearch('');
    setShowDeviceDropdown(false);
    setShowEditModal(true);
    loadTenantDevices(sub.tenantId, sub.id);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSub) return;
    setSaving(true);
    try {
      const isTrial = editForm.period === 'TRIAL';
      const expiresAtISO = editForm.expiresAt ? new Date(editForm.expiresAt).toISOString() : null;
      await adminApi.updateSubscription(editingSub.id, {
        planId: editForm.planId,
        quantity: editForm.quantity,
        pricingType: editForm.period === 'ONESHOT' ? 'ONESHOT' : 'YEARLY',
        interval: editForm.period === 'MONTHLY' ? 'MONTHLY' : 'YEARLY',
        status: isTrial ? 'TRIALING' : editForm.status,
        trialEndsAt: isTrial ? expiresAtISO : null,
        autoRenewal: editForm.autoRenewal,
        alertsEnabled: editForm.alertsEnabled,
        alertDays: editForm.alertDays,
        startDate: editForm.startDate ? new Date(editForm.startDate).toISOString() : null,
        expiresAt: expiresAtISO,
        notes: editForm.notes || null,
        deviceIds: selectedDeviceIds,
      });
      setShowEditModal(false);
      setEditingSub(null);
      loadSubscriptions();
    } catch (error) {
      console.error('Erreur mise à jour abonnement:', error);
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setCreateForm({
      tenantId: '',
      planId: '',
      quantity: 1,
      period: 'YEARLY',
      status: 'ACTIVE',
      autoRenewal: true,
      alertsEnabled: true,
      alertDays: [30, 15, 7],
      startDate: today,
      expiresAt: calcExpiresAt(today, 'YEARLY'),
      notes: '',
      deviceIds: [],
    });
    setSelectedDeviceIds([]);
    setTenantDevices([]);
    setDeviceSearch('');
    setShowDeviceDropdown(false);
    setShowCreateModal(true);
  };

  const handleSaveCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.tenantId || !createForm.planId) return;
    setCreating(true);
    try {
      const isTrial = createForm.period === 'TRIAL';
      const expiresAtISO = createForm.expiresAt ? new Date(createForm.expiresAt).toISOString() : null;
      await adminApi.createSubscription({
        tenantId: createForm.tenantId,
        planId: createForm.planId,
        quantity: createForm.quantity,
        pricingType: createForm.period === 'ONESHOT' ? 'ONESHOT' : 'YEARLY',
        interval: createForm.period === 'MONTHLY' ? 'MONTHLY' : 'YEARLY',
        status: isTrial ? 'TRIALING' : createForm.status,
        trialEndsAt: isTrial ? expiresAtISO : null,
        autoRenewal: createForm.autoRenewal,
        alertsEnabled: createForm.alertsEnabled,
        alertDays: createForm.alertDays,
        startDate: createForm.startDate ? new Date(createForm.startDate).toISOString() : null,
        expiresAt: expiresAtISO,
        notes: createForm.notes || null,
        deviceIds: selectedDeviceIds,
      });
      setShowCreateModal(false);
      loadSubscriptions();
    } catch (error) {
      console.error('Erreur création abonnement:', error);
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      ACTIVE: 'bg-green-500/20 text-green-400',
      TRIALING: 'bg-blue-500/20 text-blue-400',
      PAST_DUE: 'bg-yellow-500/20 text-yellow-400',
      CANCELED: 'bg-red-500/20 text-red-400',
      UNPAID: 'bg-orange-500/20 text-orange-400',
    };
    const labels: Record<string, string> = {
      ACTIVE: 'Actif',
      TRIALING: 'Essai',
      PAST_DUE: 'Impayé',
      CANCELED: 'Annulé',
      UNPAID: 'Non payé',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-500'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const openInvoicesModal = async (sub: Subscription) => {
    setInvoicesSub(sub);
    setShowInvoicesModal(true);
    setLoadingInvoices(true);
    try {
      const res = await adminApi.getInvoices({ subscriptionId: sub.id, limit: 50 });
      setInvoices(res.data?.invoices || []);
    } catch (err) {
      console.error('Erreur chargement factures:', err);
      setInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleGenerateInvoice = async (subscriptionId: string) => {
    setGeneratingInvoice(true);
    try {
      await adminApi.generateInvoice(subscriptionId);
      // Reload invoices
      const res = await adminApi.getInvoices({ subscriptionId, limit: 50 });
      setInvoices(res.data?.invoices || []);
    } catch (err) {
      console.error('Erreur génération facture:', err);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId: string, invoiceNumber: string | null) => {
    try {
      const res = await adminApi.downloadInvoice(invoiceId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber || invoiceId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur téléchargement facture:', err);
    }
  };

  const handleSendInvoice = async (invoiceId: string) => {
    setSendingInvoiceId(invoiceId);
    try {
      await adminApi.sendInvoice(invoiceId);
    } catch (err) {
      console.error('Erreur envoi facture:', err);
    } finally {
      setSendingInvoiceId(null);
    }
  };

  const getInvoiceStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      DRAFT: 'bg-gray-100 text-gray-500',
      OPEN: 'bg-blue-500/20 text-blue-400',
      PAID: 'bg-green-500/20 text-green-400',
      VOID: 'bg-red-500/20 text-red-400',
      UNCOLLECTIBLE: 'bg-orange-500/20 text-orange-400',
    };
    const labels: Record<string, string> = {
      DRAFT: 'Proforma',
      OPEN: 'Ouverte',
      PAID: 'Payée',
      VOID: 'Annulée',
      UNCOLLECTIBLE: 'Irrécouvrable',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-500'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const filteredDevices = tenantDevices.filter((d) => {
    // Exclure les trackers déjà liés à un autre abonnement (sauf ceux sélectionnés dans l'abonnement en cours)
    if (takenDeviceIds.has(d.id) && !selectedDeviceIds.includes(d.id)) return false;
    if (!deviceSearch) return true;
    const q = deviceSearch.toLowerCase();
    return (
      d.name.toLowerCase().includes(q) ||
      (d.plate && d.plate.toLowerCase().includes(q)) ||
      d.imei.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Abonnements</h1>
          <p className="text-gray-500">Suivi des abonnements actifs</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvel abonnement
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
              <input
                type="text"
                placeholder="Rechercher par tenant, plan, notes..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 pl-10"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          >
            <option value="">Tous les statuts</option>
            <option value="ACTIVE">Actif</option>
            <option value="TRIALING">Essai</option>
            <option value="PAST_DUE">Impayé</option>
            <option value="CANCELED">Annulé</option>
            <option value="UNPAID">Non payé</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-gray-500 text-sm">Total abonnements</p>
          <p className="text-2xl font-bold text-gray-900">{stats.totalAll}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-gray-500 text-sm">Actifs</p>
          <p className="text-2xl font-bold text-green-500">{stats.activeCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-gray-500 text-sm">Véhicules actifs</p>
          <p className="text-2xl font-bold text-blue-500">{stats.activeVehicles}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-gray-500 text-sm">Impayés</p>
          <p className="text-2xl font-bold text-amber-500">{stats.pastDueCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-gray-500 text-sm">CA annuel HT</p>
          <p className="text-2xl font-bold text-primary-600">
            {stats.annualRevenue.toLocaleString('fr-FR')}€
          </p>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-xl p-3 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-primary-700">
            {selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => handleBulkToggleActive(true)}
              disabled={bulkLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Power className="h-4 w-4" />
              Activer
            </button>
            <button
              onClick={() => handleBulkToggleActive(false)}
              disabled={bulkLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:opacity-50"
            >
              <Power className="h-4 w-4" />
              Désactiver
            </button>
            <button
              onClick={() => handleBulkToggleRenewal(true)}
              disabled={bulkLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4" />
              Renouvellement auto ON
            </button>
            <button
              onClick={() => handleBulkToggleRenewal(false)}
              disabled={bulkLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-500 text-white hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4" />
              Renouvellement auto OFF
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={bulkLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </button>
          </div>
          {bulkLoading && <Loader2 className="h-4 w-4 animate-spin text-primary-500" />}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="p-4 w-10">
                <input
                  type="checkbox"
                  checked={subscriptions.length > 0 && selectedIds.size === subscriptions.length}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                />
              </th>
              <th className="text-left p-4 text-gray-500 font-medium cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'tenant') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('tenant'); setSortOrder('asc'); } setPage(1); }}>
                Tenant {sortBy === 'tenant' && (sortOrder === 'asc' ? '▲' : '▼')}
              </th>
              <th className="text-left p-4 text-gray-500 font-medium cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'plan') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('plan'); setSortOrder('asc'); } setPage(1); }}>
                Plan {sortBy === 'plan' && (sortOrder === 'asc' ? '▲' : '▼')}
              </th>
              <th className="text-right p-4 text-gray-500 font-medium cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'quantity') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('quantity'); setSortOrder('asc'); } setPage(1); }}>
                Véhicules {sortBy === 'quantity' && (sortOrder === 'asc' ? '▲' : '▼')}
              </th>
              <th className="text-left p-4 text-gray-500 font-medium">Prix unitaire</th>
              <th className="text-right p-4 text-gray-500 font-medium">Montant HT</th>
              <th className="text-left p-4 text-gray-500 font-medium cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'status') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('status'); setSortOrder('asc'); } setPage(1); }}>
                Statut {sortBy === 'status' && (sortOrder === 'asc' ? '▲' : '▼')}
              </th>
              <th className="text-left p-4 text-gray-500 font-medium cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'currentPeriodEnd') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('currentPeriodEnd'); setSortOrder('asc'); } setPage(1); }}>
                Expiration {sortBy === 'currentPeriodEnd' && (sortOrder === 'asc' ? '▲' : '▼')}
              </th>
              <th className="text-left p-4 text-gray-500 font-medium">Renouvellement</th>
              <th className="text-center p-4 text-gray-500 font-medium">Factures</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.map((sub) => (
              <tr key={sub.id} className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${selectedIds.has(sub.id) ? 'bg-primary-50/50' : ''}`} onClick={() => openEdit(sub)}>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(sub.id)}
                    onChange={() => toggleSelect(sub.id)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-cyan-500/10">
                      <Building2 className="h-5 w-5 text-cyan-500" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">{sub.tenant.name}</p>
                      <p className="text-gray-400 text-sm">{sub.tenant.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      sub.plan.name === 'enterprise'
                        ? 'bg-purple-500/20 text-purple-400'
                        : sub.plan.name === 'pro'
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : sub.plan.name === 'basic'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {sub.plan.displayName}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <span className="font-medium text-gray-900">{sub.quantity || 1}</span>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-gray-500" />
                    <span className="text-gray-900">
                      {sub.pricingType === 'ONESHOT'
                        ? `${sub.plan.priceOneShot || sub.plan.priceYearly}€`
                        : sub.interval === 'MONTHLY'
                        ? `${(sub.plan.priceYearly / 12).toFixed(2)}€/mois`
                        : `${sub.plan.priceYearly}€/an`}
                    </span>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <span className="font-semibold text-gray-900">
                    {((sub.pricingType === 'ONESHOT'
                      ? (sub.plan.priceOneShot || sub.plan.priceYearly)
                      : sub.plan.priceYearly) * (sub.quantity || 1)).toLocaleString('fr-FR')}€
                  </span>
                  {sub.pricingType !== 'ONESHOT' && (
                    <span className="text-gray-500 text-sm">/an</span>
                  )}
                </td>
                <td className="p-4">{getStatusBadge(sub.status)}</td>
                <td className="p-4 text-sm">
                  <div className={
                    new Date(sub.currentPeriodEnd) < new Date()
                      ? 'text-red-500 font-medium'
                      : (new Date(sub.currentPeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24) <= 30
                        ? 'text-orange-500 font-medium'
                        : 'text-gray-500'
                  }>
                    {new Date(sub.currentPeriodEnd).toLocaleDateString('fr-FR')}
                  </div>
                  {sub.trialEndsAt && (
                    <div className="text-blue-400">
                      Essai jusqu'au {new Date(sub.trialEndsAt).toLocaleDateString('fr-FR')}
                    </div>
                  )}
                </td>
                <td className="p-4">
                  {sub.cancelAtPeriodEnd || sub.autoRenewal === false ? (
                    <span className="text-red-400 text-sm">Non renouvelé</span>
                  ) : sub.pricingType === 'ONESHOT' ? (
                    <span className="text-gray-400 text-sm">Achat unique</span>
                  ) : (
                    <span className="text-green-400 text-sm">Auto</span>
                  )}
                </td>
                <td className="p-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); openInvoicesModal(sub); }}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-cyan-500 transition-colors"
                      title="Voir les factures"
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleGenerateInvoice(sub.id); }}
                      className={`p-1.5 rounded-lg transition-colors ${
                        sub.status === 'TRIALING'
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'hover:bg-gray-100 text-gray-500 hover:text-green-500'
                      }`}
                      title={sub.status === 'TRIALING' ? 'Pas de facturation en période d\'essai' : 'Générer une proforma'}
                      disabled={generatingInvoice || sub.status === 'TRIALING'}
                    >
                      <FilePlus className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {subscriptions.length === 0 && (
          <div className="text-center py-12 text-gray-500">Aucun abonnement trouvé</div>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (() => {
        const totalPages = Math.ceil(total / limit);
        // Générer les numéros de pages à afficher
        const getPageNumbers = () => {
          const pages: (number | '...')[] = [];
          if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
          } else {
            pages.push(1);
            if (page > 3) pages.push('...');
            const start = Math.max(2, page - 1);
            const end = Math.min(totalPages - 1, page + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (page < totalPages - 2) pages.push('...');
            pages.push(totalPages);
          }
          return pages;
        };
        return (
          <div className="flex items-center justify-between">
            <p className="text-gray-500 text-sm">
              {(page - 1) * limit + 1}-{Math.min(page * limit, total)} sur {total} abonnements
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Préc.
              </button>
              {getPageNumbers().map((p, i) =>
                p === '...' ? (
                  <span key={`ellipsis-${i}`} className="px-2 py-1.5 text-sm text-gray-400">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      p === page
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suiv.
              </button>
            </div>
          </div>
        );
      })()}

      {/* Edit Subscription Modal */}
      {showEditModal && editingSub && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg m-4">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Modifier l'abonnement</h2>
                <p className="text-gray-500 text-sm mt-1">{editingSub.tenant.name}</p>
              </div>
              <button
                onClick={() => { setShowEditModal(false); setEditingSub(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Plan</label>
                  <select
                    value={editForm.period === 'TRIAL' ? '__trial__' : editForm.planId}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '__trial__') {
                        const newExpiresAt = calcExpiresAt(editForm.startDate, 'TRIAL');
                        setEditForm({
                          ...editForm,
                          period: 'TRIAL',
                          status: 'TRIALING',
                          expiresAt: newExpiresAt,
                          planId: editForm.planId || (plans.length > 0 ? plans[0].id : ''),
                        });
                      } else {
                        setEditForm({
                          ...editForm,
                          planId: val,
                          period: editForm.period === 'TRIAL' ? 'YEARLY' : editForm.period,
                          status: editForm.period === 'TRIAL' ? 'ACTIVE' : editForm.status,
                          expiresAt: editForm.period === 'TRIAL' ? calcExpiresAt(editForm.startDate, 'YEARLY') : editForm.expiresAt,
                        });
                      }
                    }}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    required
                  >
                    <option value="">Sélectionner un plan</option>
                    <option value="__trial__" className="text-blue-600 font-medium">Période d'essai (15 jours)</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.displayName} - {plan.priceYearly}€/an
                      </option>
                    ))}
                  </select>
                </div>
                {editForm.period === 'TRIAL' && (
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Plan associé à l'essai</label>
                    <select
                      value={editForm.planId}
                      onChange={(e) => setEditForm({ ...editForm, planId: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      required
                    >
                      <option value="">Sélectionner le plan</option>
                      {plans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.displayName} - {plan.priceYearly}€/an
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Quantité (véhicules)</label>
                    <input
                      type="number"
                      value={editForm.quantity}
                      onChange={(e) => setEditForm({ ...editForm, quantity: parseInt(e.target.value) || 1 })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      min={1}
                      required
                    />
                  </div>
                  {editForm.period !== 'TRIAL' && (
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Période</label>
                      <select
                        value={editForm.period}
                        onChange={(e) => {
                          const period = e.target.value as Period;
                          const newExpiresAt = calcExpiresAt(editForm.startDate, period);
                          setEditForm({
                            ...editForm,
                            period,
                            expiresAt: newExpiresAt,
                            status: period === 'TRIAL' ? 'TRIALING' : (editForm.status === 'TRIALING' ? 'ACTIVE' : editForm.status),
                          });
                        }}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      >
                        <option value="MONTHLY">Mensuel</option>
                        <option value="YEARLY">Annuel</option>
                        <option value="ONESHOT">Achat unique</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Multi-select trackers */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Trackers liés ({selectedDeviceIds.length})
                  </label>
                  <div className="relative" ref={deviceDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowDeviceDropdown(!showDeviceDropdown)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-left focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 flex items-center justify-between"
                    >
                      {selectedDeviceIds.length === 0
                        ? <span className="text-gray-400">Aucun tracker sélectionné</span>
                        : <span>{selectedDeviceIds.length} tracker{selectedDeviceIds.length > 1 ? 's' : ''} sélectionné{selectedDeviceIds.length > 1 ? 's' : ''}</span>
                      }
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </button>
                    {showDeviceDropdown && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
                        <div className="p-2 border-b border-gray-100">
                          <input
                            type="text"
                            value={deviceSearch}
                            onChange={(e) => setDeviceSearch(e.target.value)}
                            placeholder="Rechercher par nom, plaque, IMEI..."
                            className="w-full px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {tenantDevices.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-400">Aucun tracker pour ce tenant</div>
                          ) : filteredDevices.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-400">Aucun résultat</div>
                          ) : (
                            filteredDevices.map((device) => {
                              const isSelected = selectedDeviceIds.includes(device.id);
                              return (
                                <button
                                  key={device.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedDeviceIds((prev) =>
                                      isSelected
                                        ? prev.filter((id) => id !== device.id)
                                        : [...prev, device.id]
                                    );
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-primary-50 flex items-center gap-2 transition-colors"
                                >
                                  <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-300'}`}>
                                    {isSelected && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                  <span className="text-gray-900 truncate">{device.name}</span>
                                  {device.plate && (
                                    <span className="text-gray-400 text-xs flex-shrink-0">{device.plate}</span>
                                  )}
                                  <span className="text-gray-600 text-sm font-mono ml-auto flex-shrink-0">{device.imei}</span>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {selectedDeviceIds.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {selectedDeviceIds.map((did) => {
                        const device = tenantDevices.find((d) => d.id === did);
                        return (
                          <span
                            key={did}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs"
                          >
                            {device ? (device.plate || device.name) : did.slice(0, 8)}
                            <button
                              type="button"
                              onClick={() => setSelectedDeviceIds((prev) => prev.filter((id) => id !== did))}
                              className="hover:text-red-500"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Statut</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  >
                    <option value="ACTIVE">Actif</option>
                    <option value="TRIALING">Essai</option>
                    <option value="PAST_DUE">Impayé</option>
                    <option value="CANCELED">Annulé</option>
                    <option value="UNPAID">Non payé</option>
                  </select>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <label className="text-gray-900 font-medium">Renouvellement automatique</label>
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, autoRenewal: !editForm.autoRenewal })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      editForm.autoRenewal ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        editForm.autoRenewal ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-gray-900 font-medium">Alertes d'expiration</label>
                    <p className="text-gray-500 text-sm">Alertes à {editForm.alertDays.join(', ')} jours</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, alertsEnabled: !editForm.alertsEnabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      editForm.alertsEnabled ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        editForm.alertsEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Date de début</label>
                    <input
                      type="date"
                      value={editForm.startDate}
                      onChange={(e) => {
                        const newStart = e.target.value;
                        setEditForm({
                          ...editForm,
                          startDate: newStart,
                          expiresAt: calcExpiresAt(newStart, editForm.period),
                        });
                      }}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Date d'expiration
                    </label>
                    <input
                      type="date"
                      value={editForm.expiresAt}
                      onChange={(e) => setEditForm({ ...editForm, expiresAt: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    rows={3}
                    placeholder="Notes internes..."
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setEditingSub(null); }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Subscription Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg m-4">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Nouvel abonnement</h2>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSaveCreate}>
              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Tenant *</label>
                  <select
                    value={createForm.tenantId}
                    onChange={(e) => {
                      const tid = e.target.value;
                      setCreateForm({ ...createForm, tenantId: tid });
                      setSelectedDeviceIds([]);
                      setTenantDevices([]);
                      if (tid) loadTenantDevices(tid);
                    }}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    required
                  >
                    <option value="">Sélectionner un tenant</option>
                    {[...tenants].sort((a, b) => a.name.localeCompare(b.name)).map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Plan *</label>
                  <select
                    value={createForm.period === 'TRIAL' ? '__trial__' : createForm.planId}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '__trial__') {
                        const newExpiresAt = calcExpiresAt(createForm.startDate, 'TRIAL');
                        setCreateForm({
                          ...createForm,
                          period: 'TRIAL',
                          status: 'TRIALING',
                          expiresAt: newExpiresAt,
                          planId: createForm.planId || (plans.length > 0 ? plans[0].id : ''),
                        });
                      } else {
                        setCreateForm({
                          ...createForm,
                          planId: val,
                          period: createForm.period === 'TRIAL' ? 'YEARLY' : createForm.period,
                          status: createForm.period === 'TRIAL' ? 'ACTIVE' : createForm.status,
                          expiresAt: createForm.period === 'TRIAL' ? calcExpiresAt(createForm.startDate, 'YEARLY') : createForm.expiresAt,
                        });
                      }
                    }}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    required
                  >
                    <option value="">Sélectionner un plan</option>
                    <option value="__trial__" className="text-blue-600 font-medium">Période d'essai (15 jours)</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.displayName} - {plan.priceYearly}€/an
                      </option>
                    ))}
                  </select>
                </div>
                {createForm.period === 'TRIAL' && (
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Plan associé à l'essai</label>
                    <select
                      value={createForm.planId}
                      onChange={(e) => setCreateForm({ ...createForm, planId: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      required
                    >
                      <option value="">Sélectionner le plan</option>
                      {plans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.displayName} - {plan.priceYearly}€/an
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Quantité (véhicules)</label>
                    <input
                      type="number"
                      value={createForm.quantity}
                      onChange={(e) => setCreateForm({ ...createForm, quantity: parseInt(e.target.value) || 1 })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      min={1}
                      required
                    />
                  </div>
                  {createForm.period !== 'TRIAL' && (
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Période</label>
                      <select
                        value={createForm.period}
                        onChange={(e) => {
                          const period = e.target.value as Period;
                          const newExpiresAt = calcExpiresAt(createForm.startDate, period);
                          setCreateForm({
                            ...createForm,
                            period,
                            expiresAt: newExpiresAt,
                            status: 'ACTIVE',
                          });
                        }}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      >
                        <option value="MONTHLY">Mensuel</option>
                        <option value="YEARLY">Annuel</option>
                        <option value="ONESHOT">Achat unique</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Multi-select trackers (create) */}
                {createForm.tenantId && (
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Trackers liés ({selectedDeviceIds.length})
                    </label>
                    <div className="relative" ref={createDeviceDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setShowDeviceDropdown(!showDeviceDropdown)}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-left focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 flex items-center justify-between"
                      >
                        {selectedDeviceIds.length === 0
                          ? <span className="text-gray-400">Aucun tracker sélectionné</span>
                          : <span>{selectedDeviceIds.length} tracker{selectedDeviceIds.length > 1 ? 's' : ''} sélectionné{selectedDeviceIds.length > 1 ? 's' : ''}</span>
                        }
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </button>
                      {showDeviceDropdown && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
                          <div className="p-2 border-b border-gray-100">
                            <input
                              type="text"
                              value={deviceSearch}
                              onChange={(e) => setDeviceSearch(e.target.value)}
                              placeholder="Rechercher par nom, plaque, IMEI..."
                              className="w-full px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {tenantDevices.length === 0 ? (
                              <div className="px-4 py-2 text-sm text-gray-400">Aucun tracker pour ce tenant</div>
                            ) : filteredDevices.length === 0 ? (
                              <div className="px-4 py-2 text-sm text-gray-400">Aucun résultat</div>
                            ) : (
                              filteredDevices.map((device) => {
                                const isSelected = selectedDeviceIds.includes(device.id);
                                return (
                                  <button
                                    key={device.id}
                                    type="button"
                                    onClick={() => {
                                      setSelectedDeviceIds((prev) =>
                                        isSelected
                                          ? prev.filter((id) => id !== device.id)
                                          : [...prev, device.id]
                                      );
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-primary-50 flex items-center gap-2 transition-colors"
                                  >
                                    <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-300'}`}>
                                      {isSelected && <Check className="h-3 w-3 text-white" />}
                                    </div>
                                    <span className="text-gray-900 truncate">{device.name}</span>
                                    {device.plate && (
                                      <span className="text-gray-400 text-xs flex-shrink-0">{device.plate}</span>
                                    )}
                                    <span className="text-gray-600 text-sm font-mono ml-auto flex-shrink-0">{device.imei}</span>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {selectedDeviceIds.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {selectedDeviceIds.map((did) => {
                          const device = tenantDevices.find((d) => d.id === did);
                          return (
                            <span
                              key={did}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs"
                            >
                              {device ? (device.plate || device.name) : did.slice(0, 8)}
                              <button
                                type="button"
                                onClick={() => setSelectedDeviceIds((prev) => prev.filter((id) => id !== did))}
                                className="hover:text-red-500"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Statut</label>
                  {createForm.period === 'TRIAL' ? (
                    <div className="w-full px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm font-medium">
                      Essai (automatique)
                    </div>
                  ) : (
                    <select
                      value={createForm.status}
                      onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    >
                      <option value="ACTIVE">Actif</option>
                      <option value="TRIALING">Essai</option>
                    </select>
                  )}
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <label className="text-gray-900 font-medium">Renouvellement automatique</label>
                  <button
                    type="button"
                    onClick={() => setCreateForm({ ...createForm, autoRenewal: !createForm.autoRenewal })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      createForm.autoRenewal ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        createForm.autoRenewal ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-gray-900 font-medium">Alertes d'expiration</label>
                    <p className="text-gray-500 text-sm">Alertes à {createForm.alertDays.join(', ')} jours</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCreateForm({ ...createForm, alertsEnabled: !createForm.alertsEnabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      createForm.alertsEnabled ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        createForm.alertsEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Date de début</label>
                    <input
                      type="date"
                      value={createForm.startDate}
                      onChange={(e) => {
                        const newStart = e.target.value;
                        setCreateForm({
                          ...createForm,
                          startDate: newStart,
                          expiresAt: calcExpiresAt(newStart, createForm.period),
                        });
                      }}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Date d'expiration
                    </label>
                    <input
                      type="date"
                      value={createForm.expiresAt}
                      onChange={(e) => setCreateForm({ ...createForm, expiresAt: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={createForm.notes}
                    onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    rows={3}
                    placeholder="Notes internes..."
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={creating}>
                  {creating ? 'Création...' : 'Créer l\'abonnement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoicesModal && invoicesSub && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Factures - {invoicesSub.tenant.name}
                </h3>
                <p className="text-sm text-gray-500">
                  {invoicesSub.plan.displayName} - {invoicesSub.quantity} véhicule(s)
                </p>
              </div>
              <div className="flex items-center gap-2">
                {invoicesSub.status === 'TRIALING' ? (
                  <span className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                    Période d'essai — pas de facturation
                  </span>
                ) : (
                  <button
                    onClick={() => handleGenerateInvoice(invoicesSub.id)}
                    disabled={generatingInvoice}
                    className="btn-primary text-sm flex items-center gap-2"
                  >
                    {generatingInvoice ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FilePlus className="h-4 w-4" />
                    )}
                    Générer proforma
                  </button>
                )}
                <button onClick={() => setShowInvoicesModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="overflow-auto flex-1">
              {loadingInvoices ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12 text-gray-500">Aucune facture</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left p-3 text-gray-500 font-medium text-sm">N°</th>
                      <th className="text-left p-3 text-gray-500 font-medium text-sm">Date</th>
                      <th className="text-right p-3 text-gray-500 font-medium text-sm">Montant TTC</th>
                      <th className="text-left p-3 text-gray-500 font-medium text-sm">Statut</th>
                      <th className="text-left p-3 text-gray-500 font-medium text-sm">Échéance</th>
                      <th className="text-center p-3 text-gray-500 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-3 text-sm font-medium text-gray-900">
                          {inv.invoiceNumber || '-'}
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {new Date(inv.createdAt).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="p-3 text-sm text-right font-semibold text-gray-900">
                          {inv.amountDue.toFixed(2)} €
                        </td>
                        <td className="p-3">{getInvoiceStatusBadge(inv.status)}</td>
                        <td className="p-3 text-sm text-gray-600">
                          {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleDownloadInvoice(inv.id, inv.invoiceNumber)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-cyan-500 transition-colors"
                              title="Télécharger PDF"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleSendInvoice(inv.id)}
                              disabled={sendingInvoiceId === inv.id}
                              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-green-500 transition-colors"
                              title="Envoyer par email"
                            >
                              {sendingInvoiceId === inv.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Mail className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
